import React, { useState } from 'react';
import { Card, Tag, Button, Modal, Space } from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  FileExcelOutlined,
  FileWordOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
  ReadOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { MarkdownContent } from './MarkdownContent.jsx';

export function AttachmentsBox({ attachments = [], onSelectDocument }) {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const getFormatIcon = (format) => {
    const f = format?.toLowerCase() || '';
    if (f.includes('excel') || f.includes('csv') || f.includes('xlsx')) {
      return <FileExcelOutlined style={{ color: '#16a34a', fontSize: 22 }} />;
    }
    if (f.includes('word') || f.includes('doc')) {
      return <FileWordOutlined style={{ color: '#2563eb', fontSize: 22 }} />;
    }
    return <FilePdfOutlined style={{ color: '#dc2626', fontSize: 22 }} />;
  };

  const getFormatColor = (format) => {
    const f = format?.toLowerCase() || '';
    if (f.includes('excel') || f.includes('csv')) return 'green';
    if (f.includes('word')) return 'blue';
    return 'red';
  };

  const handleDownloadCsv = (item) => {
    if (item.csvContent) {
      const blob = new Blob(['\uFEFF' + item.csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.code || 'tai-lieu'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Đã tải file Excel/CSV: ${item.code}`);
    } else if (item.contentMarkdown) {
      const blob = new Blob([item.contentMarkdown], { type: 'text/markdown;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.code || 'tai-lieu'}.md`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Đã tải file tài liệu: ${item.code}`);
    } else {
      toast.info(`File ${item.name} đang được ban biên tập đóng gói bản phát hành.`);
    }
  };

  const handleCopyContent = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Đã sao chép nội dung tài liệu!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Card
        className="lesson-attachments"
        style={{
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          background: '#ffffff',
          marginBottom: 24,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
        }}
        styles={{ body: { padding: '20px 24px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FolderOpenOutlined style={{ fontSize: 22, color: '#0284c7' }} />
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0369a1', margin: 0 }}>
                Hệ Thống Tài Liệu, Kịch Bản & Biểu Mẫu Đính Kèm ({attachments.length} tài liệu học tập)
              </h3>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Mỗi tài liệu là một bài học thực chiến, ma trận biểu mẫu hoặc kịch bản hội thoại áp dụng ngay tại quầy.
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {attachments.map((item, idx) => (
            <div
              key={item.code || idx}
              className="lesson-attachment-row"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '14px 18px',
                background: '#ffffff',
                borderRadius: 10,
                border: '1px solid #e0f2fe',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 'min(260px, 100%)' }}>
                <div style={{ marginTop: 2 }}>{getFormatIcon(item.format)}</div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                    {item.code && (
                      <Tag color="cyan" style={{ fontSize: 11, fontWeight: 700, margin: 0 }}>
                        {item.code}
                      </Tag>
                    )}
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{item.name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    Định dạng: <Tag color={getFormatColor(item.format)} style={{ margin: 0 }}>{item.format}</Tag>
                    {item.contentMarkdown ? ' • Có sẵn toàn bộ nội dung đọc' : ''}
                    {item.csvContent ? ' • Kèm dữ liệu Excel chạy được' : ''}
                  </div>
                </div>
              </div>

              <Space size={8} wrap>
                {item.contentMarkdown && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<ReadOutlined />}
                    onClick={() => {
                      if (onSelectDocument) {
                        onSelectDocument(item);
                      } else {
                        setSelectedDoc(item);
                      }
                    }}
                    style={{ background: '#0284c7', borderColor: '#0284c7', fontWeight: 600, borderRadius: 6 }}
                  >
                    Xem & Học Tài Liệu
                  </Button>
                )}

                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadCsv(item)}
                  style={{ borderRadius: 6 }}
                >
                  {item.csvContent ? 'Tải Excel (.csv)' : 'Tải tài liệu'}
                </Button>
              </Space>
            </div>
          ))}
        </div>
      </Card>

      {/* Modal Đọc Tài Liệu Toàn Diện */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 16 }}>
            {selectedDoc && getFormatIcon(selectedDoc.format)}
            <span>
              [{selectedDoc?.code}] {selectedDoc?.name}
            </span>
          </div>
        }
        open={!!selectedDoc}
        onCancel={() => setSelectedDoc(null)}
        width={900}
        footer={[
          <Button
            key="copy"
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={() => handleCopyContent(selectedDoc?.contentMarkdown || '')}
          >
            {copied ? 'Đã sao chép' : 'Sao chép toàn bộ nội dung'}
          </Button>,
          selectedDoc?.csvContent && (
            <Button
              key="csv"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadCsv(selectedDoc)}
              style={{ background: '#16a34a', borderColor: '#16a34a' }}
            >
              Tải file Excel / CSV
            </Button>
          ),
          <Button key="close" type="default" onClick={() => setSelectedDoc(null)}>
            Đóng
          </Button>,
        ]}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '12px 8px' }}>
          {selectedDoc?.contentMarkdown ? (
            <MarkdownContent content={selectedDoc.contentMarkdown} />
          ) : (
            <p>Tài liệu này đang được hoàn thiện định dạng phát hành.</p>
          )}
        </div>
      </Modal>
    </>
  );
}
